import React, { useState, useEffect, useRef } from 'react';
import { useLang } from '../contexts/LanguageContext';
import { t } from '../utils/translations';
import api from '../utils/api';
import Layout from '../components/Layout';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, X, Loader2, Languages, Paperclip } from 'lucide-react';

export default function TicketCreatePage() {
  const { lang } = useLang();
  const navigate = useNavigate();
  const [titleFr, setTitleFr] = useState('');
  const [titleEn, setTitleEn] = useState('');
  const [descFr, setDescFr] = useState('');
  const [descEn, setDescEn] = useState('');
  const [category, setCategory] = useState('');
  const [categories, setCategories] = useState([]);
  const [assignedTo, setAssignedTo] = useState('');
  const [hashtags, setHashtags] = useState([]);
  const [hashtagInput, setHashtagInput] = useState('');
  const [users, setUsers] = useState([]);
  const [files, setFiles] = useState([]);
  const [requestPublic, setRequestPublic] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [translatingTitle, setTranslatingTitle] = useState(false);
  const [translatingDesc, setTranslatingDesc] = useState(false);
  const titleTimer = useRef(null);
  const descTimer = useRef(null);

  useEffect(() => {
    api.get('/users/assignable').then(res => {
      setUsers(res.data);
      if (res.data.length > 0) setAssignedTo(prev => prev || res.data[0]._id);
    }).catch(() => {});
    api.get('/categories').then(res => {
      setCategories(res.data);
      if (res.data.length > 0) setCategory(res.data[0].name);
    }).catch(() => {
      setCategories([{ name: 'Support Technique' }, { name: 'RH / Administratif' }, { name: 'Formation / Tutorat' }]);
      setCategory('Support Technique');
    });
  }, []);

  // Traduction title : FR→EN si lang==='fr', EN→FR si lang==='en'
  // 2 useEffect séparés gardés par `lang` pour éviter double-traduction quand
  // setTitleEn(traduction) déclenche un re-run du useEffect EN→FR (et vice-versa).
  useEffect(() => {
    if (lang !== 'fr') return;
    if (!titleFr.trim()) { setTitleEn(''); return; }
    clearTimeout(titleTimer.current);
    titleTimer.current = setTimeout(async () => {
      setTranslatingTitle(true);
      try {
        const { data } = await api.post('/translate', { text: titleFr, source_lang: 'fr', target_lang: 'en' });
        setTitleEn(data.translated_text);
      } catch {}
      setTranslatingTitle(false);
    }, 800);
    return () => clearTimeout(titleTimer.current);
  }, [titleFr, lang]);

  useEffect(() => {
    if (lang !== 'en') return;
    if (!titleEn.trim()) { setTitleFr(''); return; }
    clearTimeout(titleTimer.current);
    titleTimer.current = setTimeout(async () => {
      setTranslatingTitle(true);
      try {
        const { data } = await api.post('/translate', { text: titleEn, source_lang: 'en', target_lang: 'fr' });
        setTitleFr(data.translated_text);
      } catch {}
      setTranslatingTitle(false);
    }, 800);
    return () => clearTimeout(titleTimer.current);
  }, [titleEn, lang]);

  // Traduction description : même pattern
  useEffect(() => {
    if (lang !== 'fr') return;
    if (!descFr.trim()) { setDescEn(''); return; }
    clearTimeout(descTimer.current);
    descTimer.current = setTimeout(async () => {
      setTranslatingDesc(true);
      try {
        const { data } = await api.post('/translate', { text: descFr, source_lang: 'fr', target_lang: 'en' });
        setDescEn(data.translated_text);
      } catch {}
      setTranslatingDesc(false);
    }, 800);
    return () => clearTimeout(descTimer.current);
  }, [descFr, lang]);

  useEffect(() => {
    if (lang !== 'en') return;
    if (!descEn.trim()) { setDescFr(''); return; }
    clearTimeout(descTimer.current);
    descTimer.current = setTimeout(async () => {
      setTranslatingDesc(true);
      try {
        const { data } = await api.post('/translate', { text: descEn, source_lang: 'en', target_lang: 'fr' });
        setDescFr(data.translated_text);
      } catch {}
      setTranslatingDesc(false);
    }, 800);
    return () => clearTimeout(descTimer.current);
  }, [descEn, lang]);

  const addHashtag = () => {
    const tag = hashtagInput.trim().toLowerCase().replace(/^#/, '');
    if (tag && !hashtags.includes(tag)) setHashtags([...hashtags, tag]);
    setHashtagInput('');
  };

  const removeHashtag = (tag) => setHashtags(hashtags.filter(h => h !== tag));

  const handleFileAdd = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (files.length >= 5) { alert('Max 5 fichiers'); return; }
    if (file.size > 10 * 1024 * 1024) { alert('Max 10MB par fichier'); return; }
    setFiles([...files, file]);
    e.target.value = '';
  };

  const removeFile = (idx) => setFiles(files.filter((_, i) => i !== idx));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');
    if (!assignedTo) {
      setSubmitError(lang === 'fr'
        ? "L'assignation est obligatoire. Veuillez sélectionner un destinataire."
        : "Assignment is mandatory. Please select an assignee.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post('/tickets', {
        title_fr: titleFr, title_en: titleEn,
        description_fr: descFr, description_en: descEn,
        category, assigned_to: assignedTo, hashtags,
        request_public: requestPublic,
      });
      const ticketId = res.data._id;
      // Upload files
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);
        await api.post('/tickets/' + ticketId + '/attachments', formData, { headers: { 'Content-Type': 'multipart/form-data' } }).catch(() => {});
      }
      navigate('/tickets');
    } catch (err) {
      console.error(err);
      const detail = err?.response?.data?.detail;
      setSubmitError(typeof detail === 'string' ? detail : (lang === 'fr' ? 'Erreur lors de la création du ticket.' : 'Error creating ticket.'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-3xl mx-auto animate-fade-in">
        <button data-testid="back-button" onClick={() => navigate(-1)} className="flex items-center gap-2 mb-6 text-sm" style={{ color: '#9CA3AF' }}>
          <ArrowLeft size={16} /> {t('cancel', lang)}
        </button>

        <h1 data-testid="create-ticket-title" className="text-xl sm:text-2xl font-bold text-white mb-6">{t('create_ticket', lang)}</h1>

        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Title — input dans la langue active, preview = autre langue */}
          <div>
            <label className="block text-sm font-medium mb-1.5" style={{ color: '#9CA3AF' }}>{t('title', lang)} ({lang === 'fr' ? 'FR' : 'EN'})</label>
            <input data-testid="ticket-title-fr" value={lang === 'fr' ? titleFr : titleEn} onChange={e => (lang === 'fr' ? setTitleFr : setTitleEn)(e.target.value)} className="apollo-input" placeholder={lang === 'fr' ? 'Titre du ticket...' : 'Ticket title...'} required />
            <div className="mt-2 p-3 rounded-lg" style={{ background: 'rgba(247,127,0,0.05)', border: '1px solid rgba(247,127,0,0.1)' }}>
              <div className="flex items-center gap-2 mb-1">
                <Languages size={14} style={{ color: '#F77F00' }} />
                <span className="text-xs font-medium" style={{ color: '#F77F00' }}>{t('translation_preview', lang)} ({lang === 'fr' ? 'EN' : 'FR'})</span>
                {translatingTitle && <Loader2 size={12} className="animate-spin" style={{ color: '#F77F00' }} />}
              </div>
              <p className="text-sm" style={{ color: '#E8E8E8' }}>{(lang === 'fr' ? titleEn : titleFr) || '...'}</p>
            </div>
          </div>

          {/* Category */}
          <div>
            <label className="block text-sm font-medium mb-1.5" style={{ color: '#9CA3AF' }}>{t('category', lang)}</label>
            <select data-testid="ticket-category" value={category} onChange={e => setCategory(e.target.value)} className="apollo-input">
              {categories.map(cat => (
                <option key={cat.name} value={cat.name}>{cat.name}</option>
              ))}
            </select>
          </div>

          {/* Assigned to - REQUIRED */}
          <div>
            <label className="block text-sm font-medium mb-1.5" style={{ color: '#9CA3AF' }}>{t('assigned_to', lang)} <span style={{ color: '#EF4444' }}>*</span></label>
            <select data-testid="ticket-assignee" value={assignedTo} onChange={e => setAssignedTo(e.target.value)} className="apollo-input" required
              style={{ borderColor: !assignedTo ? 'rgba(239,68,68,0.3)' : 'rgba(255,255,255,0.08)' }}>
              <option value="" disabled>{t('select_assignee', lang)}</option>
              {users.map(u => <option key={u._id} value={u._id}>{u.name} ({t(u.role, lang)})</option>)}
            </select>
            {users.length === 0 && (
              <p className="text-xs mt-1" style={{ color: '#FBBF24' }}>{lang === 'fr' ? 'Aucun responsable disponible pour votre rôle' : 'No assignee available for your role'}</p>
            )}
            {!assignedTo && users.length > 0 && (
              <p data-testid="assignee-hint" className="text-xs mt-1" style={{ color: '#EF4444' }}>{lang === 'fr' ? "L'assignation est obligatoire. Veuillez sélectionner un destinataire." : 'Assignment is mandatory. Please select an assignee.'}</p>
            )}
          </div>

          {/* Hashtags */}
          <div>
            <label className="block text-sm font-medium mb-1.5" style={{ color: '#9CA3AF' }}>{t('hashtags', lang)}</label>
            <div className="flex gap-2 flex-wrap mb-2">
              {hashtags.map(tag => (
                <span key={tag} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium" style={{ background: 'rgba(247,127,0,0.12)', color: '#F77F00' }}>
                  #{tag} <button type="button" onClick={() => removeHashtag(tag)} className="ml-0.5"><X size={12} /></button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <input data-testid="hashtag-input" value={hashtagInput} onChange={e => setHashtagInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addHashtag(); } }}
                className="apollo-input flex-1" placeholder={t('add_hashtag', lang)} />
              <button type="button" onClick={addHashtag} className="apollo-btn-secondary px-4" style={{ minHeight: '44px', minWidth: '44px' }}>+</button>
            </div>
          </div>

          {/* Description — input dans la langue active, preview = autre langue */}
          <div>
            <label className="block text-sm font-medium mb-1.5" style={{ color: '#9CA3AF' }}>{t('description', lang)} ({lang === 'fr' ? 'FR' : 'EN'})</label>
            <textarea data-testid="ticket-description-fr" value={lang === 'fr' ? descFr : descEn} onChange={e => (lang === 'fr' ? setDescFr : setDescEn)(e.target.value)} className="apollo-input min-h-[120px] resize-y" placeholder={lang === 'fr' ? 'Description détaillée...' : 'Detailed description...'} required />
            <div className="mt-2 p-3 rounded-lg" style={{ background: 'rgba(247,127,0,0.05)', border: '1px solid rgba(247,127,0,0.1)' }}>
              <div className="flex items-center gap-2 mb-1">
                <Languages size={14} style={{ color: '#F77F00' }} />
                <span className="text-xs font-medium" style={{ color: '#F77F00' }}>{t('translation_preview', lang)} ({lang === 'fr' ? 'EN' : 'FR'})</span>
                {translatingDesc && <Loader2 size={12} className="animate-spin" style={{ color: '#F77F00' }} />}
              </div>
              <p className="text-sm whitespace-pre-wrap" style={{ color: '#E8E8E8' }}>{(lang === 'fr' ? descEn : descFr) || '...'}</p>
            </div>
          </div>

          {/* File Attachments */}
          <div>
            <label className="block text-sm font-medium mb-1.5" style={{ color: '#9CA3AF' }}>{t('attachments', lang)}</label>
            {files.length > 0 && (
              <div className="flex gap-2 flex-wrap mb-2">
                {files.map((f, i) => (
                  <span key={i} className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs" style={{ background: 'rgba(247,127,0,0.08)', color: '#F77F00', border: '1px solid rgba(247,127,0,0.15)' }}>
                    <Paperclip size={12} /> {f.name} ({(f.size / 1024).toFixed(0)} KB)
                    <button type="button" onClick={() => removeFile(i)} className="ml-1"><X size={12} /></button>
                  </span>
                ))}
              </div>
            )}
            <label className="inline-flex items-center gap-2 cursor-pointer text-xs px-3 py-2 rounded-lg" style={{ color: '#F77F00', background: 'rgba(247,127,0,0.08)', border: '1px solid rgba(247,127,0,0.15)' }}>
              <Paperclip size={14} /> {lang === 'fr' ? 'Ajouter un fichier' : 'Add file'} ({files.length}/5)
              <input data-testid="ticket-file-upload" type="file" className="hidden" onChange={handleFileAdd} accept=".jpg,.jpeg,.png,.pdf,.docx,.xlsx" />
            </label>
            <p className="text-xs mt-1" style={{ color: '#6B7280' }}>JPG, PNG, PDF, DOCX, XLSX - Max 10MB</p>
          </div>

          {/* Request Public Checkbox */}
          <div className="p-4 rounded-lg" style={{ background: 'rgba(247,127,0,0.05)', border: '1px solid rgba(247,127,0,0.1)' }}>
            <div className="flex items-start gap-3">
              <input
                data-testid="request-public-checkbox"
                type="checkbox"
                checked={requestPublic}
                onChange={e => setRequestPublic(e.target.checked)}
                className="mt-1 w-4 h-4 rounded"
                style={{ accentColor: '#F77F00' }}
              />
              <div>
                <label className="text-sm font-medium text-white cursor-pointer" onClick={() => setRequestPublic(!requestPublic)}>
                  {t('request_public', lang)}
                </label>
                <p className="text-xs mt-1" style={{ color: '#9CA3AF' }}>
                  {t('request_public_info', lang)}
                </p>
              </div>
            </div>
          </div>

          <button data-testid="submit-ticket-button" type="submit" disabled={submitting || !assignedTo} className="apollo-btn-primary w-full py-3.5 text-base" style={{ opacity: (submitting || !assignedTo) ? 0.5 : 1, minHeight: '48px' }}>
            {submitting ? t('loading', lang) : t('submit', lang)}
          </button>
          {submitError ? (
            <div data-testid="submit-error" role="alert" className="p-3 rounded-lg text-sm" style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', color: '#EF4444' }}>
              {submitError}
            </div>
          ) : null}
        </form>
      </div>
    </Layout>
  );
}
